import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { Eye, Landmark, Loader2, User } from "lucide-react";

import { Button, Modal } from "../../../ui/index.ts";
import { showError } from "../../../../utils/toast.ts";
import type { Phase2ListRow } from "../schemas/phase2-list.schema.ts";
import { EmployeeTableAvatar } from "../components/employee-table-avatar.tsx";
import { chequeFileNameFromPath } from "../utils/cancelled-cheque-filepath.ts";
import DocumentViewer from "../../../../../components/DocumentViewer.jsx";

import styles from "./bank-details-readonly-modal.module.css";

export type BankDetailsReadOnlyModalProps = {
  open: boolean;
  onClose: () => void;
  row: Phase2ListRow | null;
  fetchChequeBlob: (row: Phase2ListRow, signal?: AbortSignal) => Promise<Blob>;
};

function formatDateTime(iso: string | undefined): string {
  if (iso == null || iso === "") return "—";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function titleStatusLabel(status: string | undefined): string {
  const u = (status ?? "").toUpperCase();
  if (u === "APPROVED") return "Approved";
  if (u === "REJECTED") return "Rejected";
  if (u === "PENDING" || u === "SUBMITTED") return "Pending";
  return status?.trim() ? status : "—";
}

function extractErrorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  return "Request failed.";
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.infoRow}>
      <dt className={clsx("sub-heading-md", styles.infoLabel)}>{label}</dt>
      <dd className={clsx("body-sm", styles.infoValue)}>{value}</dd>
    </div>
  );
}

export function BankDetailsReadOnlyModal({
  open,
  onClose,
  row,
  fetchChequeBlob,
}: BankDetailsReadOnlyModalProps) {
  const [chequeLoading, setChequeLoading] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerType, setViewerType] = useState("application/octet-stream");
  const [viewerName, setViewerName] = useState("Cancelled cheque");

  const chequePath = row?.bankDetails.cancelledChequePath;

  useEffect(() => {
    if (!open) {
      setChequeLoading(false);
      setViewerOpen(false);
      setViewerUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    }
  }, [open]);

  const openCheque = useCallback(async () => {
    if (!row?.bankDetails.cancelledChequePath) return;
    setChequeLoading(true);
    try {
      const blob = await fetchChequeBlob(row);
      const url = URL.createObjectURL(blob);
      setViewerUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      setViewerType(blob.type || "application/octet-stream");
      setViewerName(chequeFileNameFromPath(row.bankDetails.cancelledChequePath));
      setViewerOpen(true);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setChequeLoading(false);
    }
  }, [row, fetchChequeBlob]);

  useEffect(() => {
    if (!viewerOpen) return;
    const element = document.getElementById("staticBackdropDocumentViewer");
    if (element) {
      const modal = new window.bootstrap.Modal(element);
      modal.show();
    }
  }, [viewerOpen]);

  const closeViewer = useCallback(() => {
    setViewerOpen(false);
    setViewerUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  if (row == null) return null;

  const b = row.bankDetails;
  const statusLabel = titleStatusLabel(
    row.approvalStatus ?? b.approvalStatus ?? row.bankStatus,
  );
  const approvedOn = formatDateTime(row.submittedOn);

  const footer = (
    <div className={styles.footer}>
      <Button type="button" variant="light-grey" onClick={onClose}>
        Close
      </Button>
    </div>
  );

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={`Bank Details `}
        size="xl"
        className={styles.modal}
        footer={footer}
      >
        <div className={styles.body}>
          <div className={styles.infoGrid}>
            <section className={styles.infoPanel}>
              <h3 className={styles.panelHeading}>
                <User size={18} strokeWidth={2} aria-hidden />
                Employee Information
              </h3>

              <dl className={styles.infoList}>
                <InfoRow label="Employee Name" value={row.employeeName} />
                <InfoRow label="Employee Code" value={row.employeeCode} />
                <InfoRow
                  label="Department"
                  value={row.designation?.trim() ? row.designation : "—"}
                />
                <InfoRow label="Email" value={row.email} />
                <InfoRow label="Approved On" value={approvedOn} />
              </dl>
            </section>

            <section className={styles.infoPanel}>
              <h3 className={styles.panelHeading}>
                <Landmark size={18} strokeWidth={2} aria-hidden />
                Bank Information
              </h3>
              <dl className={styles.infoList}>
                <InfoRow
                  label="Beneficiary Name"
                  value={b.beneficiaryName ?? "—"}
                />
                <InfoRow label="Account Number" value={b.accountNumber ?? "—"} />
                <InfoRow label="Bank Name" value={b.bankName ?? "—"} />
                <InfoRow label="Branch Name" value={b.branchName ?? "—"} />
                <InfoRow label="IFSC Code" value={b.ifscCode ?? "—"} />
                <div className={styles.infoRow}>
                  <dt className={clsx("sub-heading-md", styles.infoLabel)}>
                    Cancelled Cheque
                  </dt>
                  <dd className={styles.infoValue}>
                    {chequePath ? (
                      <button
                        type="button"
                        className={styles.viewLink}
                        disabled={chequeLoading}
                        onClick={() => void openCheque()}
                      >
                        {chequeLoading ? (
                          <Loader2
                            size={14}
                            className={styles.spin}
                            aria-hidden
                          />
                        ) : (
                          <Eye size={14} aria-hidden />
                        )}
                        {chequeLoading ? "Loading…" : "View"}
                      </button>
                    ) : (
                      "—"
                    )}
                  </dd>
                </div>
              </dl>
            </section>
          </div>
        </div>
      </Modal>

      {viewerOpen && viewerUrl ? (
        <DocumentViewer
          url={viewerUrl}
          contentType={viewerType}
          fileName={viewerName}
          onClose={closeViewer}
        />
      ) : null}
    </>
  );
}
